import { type ReactNode, createContext, useContext } from "react"
import type { PairingInvite, PairingInviteShare } from "./pairing"
import type { AccessLevel, PersonRecord } from "./people"
import type { AddRemoteComputerInput, ComputerBackend, ComputerListState, ComputerOverview, ComputerScope, OverviewBatchResult } from "./types"

export interface PairingSession {
    code: string
    endpoint: string
    expiresAt: string
    inviteUrl: string
    nodeId: string
    pushEnrollment: boolean
    /** Present for share invites: who is sharing, at what level, plus the chat-ready message. */
    share?: PairingInviteShare & { message: string; principalId: string }
}

export interface ShareRequest {
    personName: string
    level: AccessLevel
    workspaceIds: string[]
    projectName?: string
    expiresAt?: string
}

export interface ComputerContextValue {
    backend: ComputerBackend
    state: ComputerListState
    scope: ComputerScope
    overviews: Record<string, ComputerOverview>
    overviewBatch: OverviewBatchResult | null
    busy: boolean
    error: string | null
    /** Colleagues whose computers are registered here through a share. */
    people: PersonRecord[]
    selectScope(scope: ComputerScope): Promise<boolean>
    addComputer(input: AddRemoteComputerInput): Promise<void>
    pairAndAdd(invite: PairingInvite): Promise<void>
    removeComputer(nodeId: string): Promise<void>
    refreshAccess(nodeId: string): Promise<void>
    startPairing(nodeId: string): Promise<PairingSession>
    startShare(nodeId: string, request: ShareRequest): Promise<PairingSession>
    /** Mints a zero-access person invite on one of this client's computers for code-word linking. */
    startLink(nodeId: string, personName: string): Promise<{ invite: string; principalId: string; nodeId: string }>
    refreshOverviews(): Promise<void>
}

const ComputerContext = createContext<ComputerContextValue | null>(null)

export function ComputerProvider({ value, children }: { value: ComputerContextValue; children: ReactNode }) {
    return <ComputerContext.Provider value={value}>{children}</ComputerContext.Provider>
}

export function useOptionalComputers(): ComputerContextValue | null {
    return useContext(ComputerContext)
}

export function useComputers(): ComputerContextValue {
    const value = useContext(ComputerContext)
    if (!value) throw new Error("useComputers must be used within ComputerProvider")
    return value
}
